import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Center, Spinner } from '@chakra-ui/react';
import appBranding from '@happylife-a/branding';
import useLoadYandexMaps from './useLoadYandexMaps';
import { theme } from '../../theme';

export default function ClusteredYandexMap({
  markers = [],
  height = '500px',
  onAddressSelect,
  selectedAddress,
}) {
  const brandEnv = appBranding.configs.environments;
  const coordinatesFields = appBranding.fields.brand.CountryParam.Map.InitialCenter;

  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const allPlacemarksRef = useRef([]);
  const [isMapReady, setIsMapReady] = useState(false);

  const initialCenter = [
    brandEnv.getField(coordinatesFields.Latitude),
    brandEnv.getField(coordinatesFields.Longitude),
  ];

  const markerIcon = useMemo(
    () =>
      'data:image/svg+xml;charset=UTF-8,' +
      encodeURIComponent(`
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="20" fill="${theme.colors.primary[500]}" />
          <circle cx="20.001" cy="20.0001" r="9.47368" fill="white" />
        </svg>
      `),
    []
  );

  const createPlacemark = useCallback(
    (coordinates, balloonContent) => {
      return new window.ymaps.Placemark(
        coordinates,
        { balloonContent },
        {
          iconLayout: 'default#image',
          iconImageHref: markerIcon,
          iconImageSize: [30, 42],
          iconImageOffset: [-15, -42],
        }
      );
    },
    [markerIcon]
  );

  const reverseGeocode = useCallback(async (coords) => {
    try {
      const res = await window.ymaps.geocode(coords);
      const geo = res.geoObjects.get(0);

      return {
        address: geo.getAddressLine(),
        city: geo.getLocalities()[0] || '',
        country: geo.getCountry() || '',
        region: geo.getAdministrativeAreas()[0] || '',
        latitude: coords[0],
        longitude: coords[1],
        zipCode: '',
        accuracy: 1,
      };
    } catch (err) {
      return {
        address: `Lat: ${coords[0]}, Lng: ${coords[1]}`,
        city: '',
        country: '',
        region: '',
        latitude: coords[0],
        longitude: coords[1],
        zipCode: '',
        accuracy: 0,
      };
    }
  }, []);

  useLoadYandexMaps(() => {
    if (!window.ymaps || mapRef.current) {
      return;
    }

    const clusterIconContentLayout = window.ymaps.templateLayoutFactory.createClass(
      `<div style="
          background: ${theme.colors.primary[500]};
          color: white;
          border-radius: 50%;
          border-width: 3px;
          border-color: white;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;">
          $[properties.geoObjects.length]
        </div>`
    );

    mapRef.current = new window.ymaps.Map('clustered-map', {
      center: initialCenter,
      zoom: 12,
      controls: ['zoomControl', 'geolocationControl'],
    });

    clustererRef.current = new window.ymaps.Clusterer({
      clusterIcons: [
        {
          href:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(`
              <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="20" fill="${theme.colors.primary[500]}"/>
              </svg>
            `),
          size: [40, 40],
          offset: [-20, -20],
        },
      ],
      clusterIconContentLayout: clusterIconContentLayout,
      groupByCoordinates: false,
      clusterDisableClickZoom: false,
      clusterOpenBalloonOnClick: true,
    });

    setIsMapReady(true);
  }, []);

  useEffect(() => {
    if (!isMapReady || !window.ymaps || !mapRef.current) {
      return;
    }

    allPlacemarksRef.current = markers.map((marker) => {
      const coords = [marker.coordinates.latitude, marker.coordinates.longitude];
      const placemark = createPlacemark(coords, marker.name);

      placemark.events.add('click', async () => {
        const data = await reverseGeocode(coords);
        onAddressSelect?.(marker, data);
      });

      return { placemark, coords };
    });

    const bounds = mapRef.current.getBounds();
    const isInBounds = (coords) =>
      coords[0] >= bounds[0][0] &&
      coords[0] <= bounds[1][0] &&
      coords[1] >= bounds[0][1] &&
      coords[1] <= bounds[1][1];

    const visiblePlacemarks = allPlacemarksRef.current
      .filter(({ coords }) => isInBounds(coords))
      .map(({ placemark }) => placemark);

    clustererRef.current.add(visiblePlacemarks);
    mapRef.current.geoObjects.add(clustererRef.current);
  }, [isMapReady, markers, createPlacemark, reverseGeocode, onAddressSelect]);

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !clustererRef.current) {
      return;
    }

    const updateVisibleMarkers = () => {
      const bounds = mapRef.current.getBounds();
      const isInBounds = (coords) =>
        coords[0] >= bounds[0][0] &&
        coords[0] <= bounds[1][0] &&
        coords[1] >= bounds[0][1] &&
        coords[1] <= bounds[1][1];

      const filtered = allPlacemarksRef.current
        .filter(({ coords }) => isInBounds(coords))
        .map(({ placemark }) => placemark);

      clustererRef.current.removeAll();
      clustererRef.current.add(filtered);
    };

    mapRef.current.events.add('boundschange', updateVisibleMarkers);

    return () => {
      mapRef.current.events.remove('boundschange', updateVisibleMarkers);
    };
  }, [isMapReady]);

  useEffect(() => {
    if (
      isMapReady &&
      selectedAddress &&
      selectedAddress.latitude &&
      selectedAddress.longitude &&
      mapRef.current
    ) {
      const coords = [selectedAddress.latitude, selectedAddress.longitude];

      const currentCenter = mapRef.current.getCenter();
      const isSame =
        Math.abs(currentCenter[0] - coords[0]) < 0.0001 &&
        Math.abs(currentCenter[1] - coords[1]) < 0.0001;

      if (!isSame) {
        const currentZoom = mapRef.current.getZoom();
        const targetZoom = currentZoom < 15 ? 15 : currentZoom;

        mapRef.current.setCenter(coords, targetZoom, {
          checkZoomRange: true,
          duration: 400,
          timingFunction: 'ease-in-out',
        });
      }
    }
  }, [selectedAddress, isMapReady]);

  if (initialCenter.some((v) => v === undefined)) {
    return (
      <Center h="full">
        <Spinner />
      </Center>
    );
  }

  return <Box id="clustered-map" style={{ width: '100%', height }} />;
}
